/*
 * LatestNewsGrid — homepage tail block.
 *
 * Renders a paginated grid of the newest active news. Users can click
 * "Daha Fazla Yükle" to fetch older items without leaving the homepage.
 * Filtered by the selected province with a global fallback so smaller
 * provinces never see an empty list.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useProvince } from "@/contexts/ProvinceContext";
import { ArrowRight, Newspaper } from "lucide-react";

const PAGE_SIZE = 12;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("tr-TR", {
      day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit",
    });
  } catch { return ""; }
};

export default function LatestNewsGrid() {
  const { selected } = useProvince();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadPage = useCallback(async (startSkip) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        skip: String(startSkip),
        include_global: "true",
      });
      if (selected?.id) params.set("province_id", selected.id);
      const r = await api.get(`/news?${params.toString()}`);
      const list = r.data.items || [];
      setTotal(r.data.total || 0);
      setItems((prev) => (startSkip === 0 ? list : [...prev, ...list]));
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [selected?.id]);

  useEffect(() => {
    setSkip(0);
    loadPage(0);
  }, [loadPage]);

  const handleMore = () => {
    const next = skip + PAGE_SIZE;
    setSkip(next);
    loadPage(next);
  };

  if (!items.length && !loading) return null;

  return (
    <section data-testid="latest-news-grid" className="w-full">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-1 h-6 bg-[#e11d2a] rounded-full" />
          <h3 className="text-lg font-black flex items-center gap-2">
            <Newspaper className="w-5 h-5 text-[#e11d2a]" />
            TÜM HABERLER
          </h3>
        </div>
        <Link
          to="/haberler"
          data-testid="latest-news-all-link"
          className="text-xs font-semibold text-[#e11d2a] hover:underline flex items-center gap-1"
        >
          Tümünü Gör <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        {items.map((n) => (
          <Link
            key={n.id}
            to={`/haber/${n.slug}`}
            data-testid={`latest-news-item-${n.id}`}
            className="group bg-white border border-gray-100 rounded-lg overflow-hidden hover:shadow-md hover:border-gray-200 transition-shadow"
          >
            <div className="aspect-[16/10] overflow-hidden bg-gray-100">
              {n.cover_image ? (
                <img
                  src={n.cover_image}
                  alt={n.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300">
                  <Newspaper className="w-8 h-8" />
                </div>
              )}
            </div>
            <div className="p-3">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                {n.category && (
                  <span
                    className="text-[10px] font-bold uppercase tracking-wide"
                    style={{ color: n.category.color }}
                  >
                    {n.category.name}
                  </span>
                )}
                {n.province && (
                  <span className="text-[10px] text-gray-500">• {n.province.name}</span>
                )}
              </div>
              <h4 className="text-sm font-semibold leading-snug line-clamp-2 group-hover:text-[#e11d2a] transition-colors">
                {n.title}
              </h4>
              {n.spot && (
                <p className="text-xs text-gray-500 mt-1 line-clamp-2">{n.spot}</p>
              )}
              <div className="text-[10px] text-gray-400 mt-2">
                {fmtDate(n.publish_at || n.created_at)}
              </div>
            </div>
          </Link>
        ))}
      </div>

      {items.length < total && (
        <div className="flex justify-center mt-5">
          <button
            type="button"
            onClick={handleMore}
            disabled={loading}
            data-testid="latest-news-load-more"
            className="px-6 py-2 border border-gray-200 rounded-md text-sm font-semibold hover:bg-gray-50 hover:border-[#e11d2a] hover:text-[#e11d2a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Yükleniyor…" : "Daha Fazla Yükle"}
          </button>
        </div>
      )}
    </section>
  );
}
